import type { App } from "@open-booking/server";
import type { CreateContractTemplatePayload, UpdateContractTemplatePayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetContractTemplatesResponse = App["~Routes"]["contract-templates"]["get"]["response"]["200"];
type CreateContractTemplateResponse = App["~Routes"]["contract-templates"]["post"]["response"]["200"];
type UpdateContractTemplateResponse = App["~Routes"]["contract-templates"][":id"]["put"]["response"]["200"];
type DeleteContractTemplateResponse = App["~Routes"]["contract-templates"][":id"]["delete"]["response"]["200"];

export type ContractTemplate = GetContractTemplatesResponse["contractTemplates"][number];

export const contractTemplateApi = createApi({
	reducerPath: "contractTemplateApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["ContractTemplate"],
	endpoints: (builder) => ({
		// biome-ignore lint/suspicious/noConfusingVoidType: RTK Query uses `| void` so the hook accepts no argument
		getContractTemplates: builder.query<GetContractTemplatesResponse, { search?: string } | void>({
			query:
				(params): EdenCall<GetContractTemplatesResponse> =>
				(client) =>
					client["contract-templates"].get({ query: params ?? {} }),
			providesTags: ["ContractTemplate"],
		}),
		createContractTemplate: builder.mutation<CreateContractTemplateResponse, CreateContractTemplatePayload>({
			query:
				(payload): EdenCall<CreateContractTemplateResponse> =>
				(client) =>
					client["contract-templates"].post(payload),
			invalidatesTags: ["ContractTemplate"],
		}),
		updateContractTemplate: builder.mutation<UpdateContractTemplateResponse, { id: string; data: UpdateContractTemplatePayload }>({
			query:
				({ id, data }): EdenCall<UpdateContractTemplateResponse> =>
				(client) =>
					client["contract-templates"]({ id }).put(data),
			invalidatesTags: ["ContractTemplate"],
		}),
		deleteContractTemplate: builder.mutation<DeleteContractTemplateResponse, string>({
			query:
				(id): EdenCall<DeleteContractTemplateResponse> =>
				(client) =>
					client["contract-templates"]({ id }).delete(),
			invalidatesTags: ["ContractTemplate"],
		}),
	}),
});

export const {
	useGetContractTemplatesQuery,
	useCreateContractTemplateMutation,
	useUpdateContractTemplateMutation,
	useDeleteContractTemplateMutation,
} = contractTemplateApi;
